import { useCallback, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useQueryClient } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { useAccountAlias, useUserId } from "resources/account"
import { getOwnSkillsQueryKey } from "resources/skills"
import { getSkillSettingsQueryKey } from "resources/skill-settings"
import {
  type AddedSkill,
  type SkillProposal,
  type SkillValidationResult,
  narrowAddedSkill,
  narrowSkillValidationResult,
} from "connected-components/agent-chat/chatbox/skill-proposal-contract"

export type AddSkillFailure =
  | { kind: "nameTaken", message: string | null }
  | { kind: "replaceTargetGone", message: string | null }
  | { kind: "rejected", message: string | null }
  | { kind: "tooLarge" }
  | { kind: "retryable" }

export interface SkillUploadInput {
  bundle: string
  replacesSkillId?: string
}

export type AddSkillProposalInput = SkillProposal | SkillUploadInput

function isSkillProposal(input: AddSkillProposalInput): input is SkillProposal {
  return "proposalId" in input
}

export interface AddSkillProposalState {
  add: (proposal: AddSkillProposalInput, options?: { asNew?: boolean }) => Promise<AddedSkill | null>
  isAdding: boolean
  failure: AddSkillFailure | null
  added: AddedSkill | null
  reset: () => void
}

function failureOf(error: unknown, sentReplacesSkillId: string | null): AddSkillFailure {
  const code = (error as { code?: unknown } | null)?.code
  if (code === 409) return { kind: "nameTaken", message: serverMessageOf(error) }
  if (code === 404 && sentReplacesSkillId !== null) {
    return { kind: "replaceTargetGone", message: serverMessageOf(error) }
  }
  if (code === 400) return { kind: "rejected", message: serverMessageOf(error) }
  if (code === 413) return { kind: "tooLarge" }
  return { kind: "retryable" }
}

function serverMessageOf(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null
  const { error: parsed, errors, response } = error as { error?: unknown, errors?: unknown, response?: unknown }
  if (typeof parsed === "string") return sentenceOf(parsed)
  if (Array.isArray(errors)) {
    const first = errors.find((entry) => typeof (entry as { message?: unknown })?.message === "string")
    if (first) return sentenceOf((first as { message: string }).message)
  }
  if (typeof response !== "string") return null
  try {
    const body: unknown = JSON.parse(response)
    const message = (body as { error?: unknown } | null)?.error
    return typeof message === "string" ? sentenceOf(message) : null
  } catch {
    return null
  }
}

function sentenceOf(value: string): string | null {
  const text = value.trim()
  return text === "" || text.startsWith("<") || text.length > 200 ? null : text
}

export function useAddSkillProposal(): AddSkillProposalState {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const [isAdding, setIsAdding] = useState(false)
  const [failure, setFailure] = useState<AddSkillFailure | null>(null)
  const [added, setAdded] = useState<AddedSkill | null>(null)

  const add = useCallback(async (proposal: AddSkillProposalInput, { asNew = false }: { asNew?: boolean } = {}) => {
    const isProposal = isSkillProposal(proposal)
    if (isProposal && proposal.bundle === null) return null
    setIsAdding(true)
    setFailure(null)
    const sentReplacesSkillId = asNew ? null : proposal.replacesSkillId ?? null
    const body: Record<string, unknown> = isProposal
      ? {
        bundle: proposal.bundle,
        proposalId: proposal.proposalId,
        ...(proposal.sourceThreadId != null && { sourceThreadId: proposal.sourceThreadId }),
      }
      : { bundle: proposal.bundle }
    try {
      const raw = await new Promise<unknown>((resolve, reject) => {
        dispatch(sentReplacesSkillId === null
          ? Creators.post(`/api/${accountAlias}/skills`, body, resolve, reject)
          : Creators.put(`/api/${accountAlias}/skills/${encodeURIComponent(sentReplacesSkillId)}`, body, resolve, reject))
      })
      const skill = narrowAddedSkill(raw)
      if (skill === null) {
        setFailure({ kind: "retryable" })
        return null
      }
      setAdded(skill)
      void queryClient.invalidateQueries({ queryKey: getOwnSkillsQueryKey(accountAlias, userId) })
      void queryClient.invalidateQueries({ queryKey: getSkillSettingsQueryKey(accountAlias) })
      return skill
    } catch (error) {
      setFailure(failureOf(error, sentReplacesSkillId))
      return null
    } finally {
      setIsAdding(false)
    }
  }, [accountAlias, userId, dispatch, queryClient])

  const reset = useCallback(() => {
    setFailure(null)
    setAdded(null)
  }, [])

  return { add, isAdding, failure, added, reset }
}

export interface ValidateSkillUploadState {
  validate: (bundle: string) => Promise<SkillValidationResult | null>
  isValidating: boolean
  failure: AddSkillFailure | null
  reset: () => void
}

export function useValidateSkillUpload(): ValidateSkillUploadState {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()
  const [isValidating, setIsValidating] = useState(false)
  const [failure, setFailure] = useState<AddSkillFailure | null>(null)
  const requestTokenRef = useRef(0)

  const validate = useCallback(async (bundle: string) => {
    requestTokenRef.current += 1
    const token = requestTokenRef.current
    setIsValidating(true)
    setFailure(null)
    try {
      const raw = await new Promise<unknown>((resolve, reject) => {
        dispatch(Creators.post(`/api/${accountAlias}/skills/validate`, { bundle }, resolve, reject))
      })
      const result = narrowSkillValidationResult(raw)
      if (result === null) {
        if (requestTokenRef.current === token) setFailure({ kind: "retryable" })
        return null
      }
      return result
    } catch (error) {
      if (requestTokenRef.current === token) setFailure(failureOf(error, null))
      return null
    } finally {
      if (requestTokenRef.current === token) setIsValidating(false)
    }
  }, [accountAlias, dispatch])

  const reset = useCallback(() => {
    requestTokenRef.current += 1
    setFailure(null)
    setIsValidating(false)
  }, [])

  return { validate, isValidating, failure, reset }
}
